import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type {
  CreatePlaceCommentInput,
  CreatePlaceInput,
  Place,
  PlaceComment,
  PlacesFilter,
  ReportPlaceInput,
  ShareToFeedInput,
} from '@cobu/types'
import { isCommentModeration } from '@cobu/types'
import placesService from './placesService'

export const PLACES_KEYS = {
  all: ['places'] as const,
  list: (buildingId: string, filter: PlacesFilter) => ['places', buildingId, filter] as const,
  detail: (placeId: string) => ['places', 'detail', placeId] as const,
  comments: (placeId: string) => ['places', 'comments', placeId] as const,
}

/**
 * Patch a single place across every cached list query so an edit made on the
 * detail screen (like/been) is reflected in the list cell on navigate-back,
 * without waiting for the list's staleTime to lapse. Detail/comment caches have
 * no `pages`, so the updater is a no-op for them.
 */
const patchPlaceInLists = (queryClient: QueryClient, placeId: string, patch: Partial<Place>) => {
  queryClient.setQueriesData<{ pages: Place[][]; pageParams: unknown[] }>(
    { queryKey: PLACES_KEYS.all },
    (data) => {
      if (!data || !Array.isArray(data.pages)) return data
      return {
        ...data,
        pages: data.pages.map((page) =>
          page.map((p) => (p.uuid === placeId ? { ...p, ...patch } : p)),
        ),
      }
    },
  )
}

/**
 * Paginated place list (1-indexed pages). Page size is fixed server-side, so we
 * keep requesting the next page until one comes back short/empty.
 */
export const usePlacesQuery = (buildingId: string, filter: PlacesFilter = {}) => {
  return useInfiniteQuery({
    queryKey: PLACES_KEYS.list(buildingId, filter),
    queryFn: ({ pageParam }) => placesService.getPlaces(buildingId, filter, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => (lastPage.length > 0 ? allPages.length + 1 : undefined),
    enabled: !!buildingId,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export const usePlaceQuery = (placeId: string) => {
  return useQuery({
    queryKey: PLACES_KEYS.detail(placeId),
    queryFn: () => placesService.getPlace(placeId),
    enabled: !!placeId,
    // A 404 is "not visible to you" — don't hammer the server retrying it.
    retry: false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export const usePlaceCommentsQuery = (placeId: string) => {
  return useQuery({
    queryKey: PLACES_KEYS.comments(placeId),
    queryFn: () => placesService.getComments(placeId),
    enabled: !!placeId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

/**
 * Neighbors who liked / have "been" to a place — for the people-list bottom
 * sheet. Only fetches while the sheet is open (`enabled`), and keyed by type so
 * the like and been lists cache independently.
 */
export const usePlaceReactionPeopleQuery = (placeId: string, type: 'like' | 'been', enabled: boolean) => {
  return useQuery({
    queryKey: [...PLACES_KEYS.detail(placeId), 'reactionPeople', type],
    queryFn: () => placesService.getReactionPeople(placeId, type),
    enabled: enabled && !!placeId,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export const useCreatePlaceMutation = (buildingId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    // Attribution is derived server-side from the auth token — no addedBy needed.
    mutationFn: ({ input }: { input: CreatePlaceInput }) =>
      placesService.createPlace(buildingId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.all })
    },
  })
}

/** Boolean per-user like toggle. Optimistic on count+flag; reconciled from the server result. */
export const useToggleLikeMutation = (placeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ liked }: { liked: boolean }) => placesService.toggleLike(placeId, liked),
    onMutate: async ({ liked }) => {
      const key = PLACES_KEYS.detail(placeId)
      const previous = queryClient.getQueryData<Place>(key)
      // Guard against rapid re-taps: only adjust the count when the state is
      // actually changing, so a double-fire can't double-count before the
      // server response reconciles.
      if (previous && previous.myLike !== liked) {
        queryClient.setQueryData<Place>(key, {
          ...previous,
          myLike: liked,
          likeCount: Math.max(0, previous.likeCount + (liked ? 1 : -1)),
        })
      }
      return { previous }
    },
    onSuccess: (result) => {
      const key = PLACES_KEYS.detail(placeId)
      const current = queryClient.getQueryData<Place>(key)
      if (current) {
        queryClient.setQueryData<Place>(key, { ...current, likeCount: result.likeCount, myLike: result.myLike })
      }
      patchPlaceInLists(queryClient, placeId, { likeCount: result.likeCount, myLike: result.myLike })
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PLACES_KEYS.detail(placeId), context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.detail(placeId) })
    },
  })
}

export const useToggleBeenMutation = (placeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ been }: { been: boolean }) => placesService.toggleBeen(placeId, been),
    onMutate: async ({ been }) => {
      const key = PLACES_KEYS.detail(placeId)
      const previous = queryClient.getQueryData<Place>(key)
      // Guard against rapid re-taps (see useToggleLikeMutation).
      if (previous && previous.myBeen !== been) {
        queryClient.setQueryData<Place>(key, {
          ...previous,
          myBeen: been,
          beenCount: Math.max(0, previous.beenCount + (been ? 1 : -1)),
        })
      }
      return { previous }
    },
    onSuccess: (result) => {
      const key = PLACES_KEYS.detail(placeId)
      const current = queryClient.getQueryData<Place>(key)
      if (current) {
        queryClient.setQueryData<Place>(key, { ...current, beenCount: result.beenCount, myBeen: result.myBeen })
      }
      patchPlaceInLists(queryClient, placeId, { beenCount: result.beenCount, myBeen: result.myBeen })
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PLACES_KEYS.detail(placeId), context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.detail(placeId) })
    },
  })
}

/**
 * Create a place comment. Resolves to either the created comment or a
 * moderation verdict (see placesService.createComment) — the caller decides
 * which, via isCommentModeration. Nothing is invalidated on a blocked comment
 * since no comment was written, and refetching would just churn the list.
 */
export const useCreatePlaceCommentMutation = (placeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ input }: { input: CreatePlaceCommentInput }) => placesService.createComment(input),
    onSuccess: (result) => {
      if (isCommentModeration(result)) return
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.comments(placeId) })
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.detail(placeId) })
    },
  })
}

/** Delete a place comment. Author-or-admin only; the server 403s otherwise. */
export const useDeletePlaceCommentMutation = (placeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string }) => placesService.deleteComment(placeId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.comments(placeId) })
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.detail(placeId) })
    },
  })
}

/**
 * Toggle the current user's heart reaction on a place comment. The place UI only
 * ever sends one fixed reaction name; passing `hasReacted` lets the same hook add
 * or remove. Optimistic on the comment's count + myReaction; reconciled from the
 * server's authoritative { reactionsCount, myReaction }.
 */
export const useReactToPlaceCommentMutation = (placeId: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, reactionName, hasReacted }: { commentId: string; reactionName: string; hasReacted: boolean }) =>
      hasReacted
        ? placesService.unreactToComment(placeId, commentId)
        : placesService.reactToComment(placeId, commentId, reactionName),
    onMutate: async ({ commentId, reactionName, hasReacted }) => {
      const key = PLACES_KEYS.comments(placeId)
      const previous = queryClient.getQueryData<PlaceComment[]>(key)
      if (previous) {
        queryClient.setQueryData<PlaceComment[]>(
          key,
          previous.map((c) =>
            c.uuid === commentId
              ? {
                  ...c,
                  myReaction: hasReacted ? null : reactionName,
                  reactionsCount: Math.max(0, c.reactionsCount + (hasReacted ? -1 : 1)),
                }
              : c,
          ),
        )
      }
      return { previous }
    },
    onSuccess: (result, { commentId }) => {
      const key = PLACES_KEYS.comments(placeId)
      const current = queryClient.getQueryData<PlaceComment[]>(key)
      if (current) {
        queryClient.setQueryData<PlaceComment[]>(
          key,
          current.map((c) =>
            c.uuid === commentId
              ? { ...c, reactionsCount: result.reactionsCount, myReaction: result.myReaction }
              : c,
          ),
        )
      }
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(PLACES_KEYS.comments(placeId), context.previous)
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: PLACES_KEYS.comments(placeId) })
    },
  })
}

export const useShareToFeedMutation = () => {
  return useMutation({
    mutationFn: (input: ShareToFeedInput) => placesService.shareToFeed(input),
  })
}

export const useReportPlaceMutation = () => {
  return useMutation({
    mutationFn: (input: ReportPlaceInput) => placesService.reportPlace(input),
  })
}
